import { Calendar } from "@heroui/calendar";
import { CalendarDate } from "@internationalized/date";
import { isSameDay } from "date-fns";
import dayjs from "dayjs";
import { CalendarIcon, X } from "lucide-react";
import { useState } from "react";
import { getEventImageDate } from "~/lib/helperFuncation/formateDate";
import { cn } from "~/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { useFontStyle } from "~/hook/useFontStyle";

type Props = {
  setDate: (date: string) => void;
  selectedDate: string;
  availableDataDatesArray: string[];
};

export default function DailydarshanSelectedDate({ setDate, selectedDate, availableDataDatesArray }: Props) {
  const { normalInter } = useFontStyle();
  const availableDateObjects = availableDataDatesArray?.map((d) => new Date(d));
  const { month, day, year } = getEventImageDate(selectedDate);
  const [open, setOpen] = useState(false);
  const [isPickerOpen, setIsPickerOpen] = useState(false);

  const isAvailable = (date: Date) => availableDateObjects?.some((availableDate) => isSameDay(availableDate, date));

  return (
    <div className="w-full max-w-md mx-auto my-2 relative">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            className={cn(
              "w-full py-2 px-3 flex justify-between items-center rounded-full border border-borderPrimary gap-3 cursor-pointer capitalize text-textHeadingColor",
              normalInter
            )}
          >
            {selectedDate ? `${day} ${month}, ${year}` : <span>Pick a date</span>}
            <CalendarIcon size={20} />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0 shadow-none border-none bg-transparent" align="center">
          <Calendar
            showMonthAndYearPickers
            aria-label="Date (Invalid on weekends)"
            isHeaderExpanded={isPickerOpen}
            onHeaderExpandedChange={setIsPickerOpen}
            value={
              selectedDate
                ? (() => {
                    const d = dayjs(selectedDate, ["DD MMM YYYY", "YYYY-MM-DD"]);
                    return new CalendarDate(d.year(), d.month() + 1, d.date());
                  })()
                : undefined
            }
            onChange={(dateValue) => {
              if (dateValue) {
                // Convert CalendarDate to JS Date for form state
                const jsDate = new Date(dateValue.year, dateValue.month - 1, dateValue.day);
                setDate(dayjs(jsDate).format("YYYY-MM-DD")); // Update local state for date
                setOpen(false); // Close the calendar popover
              }
            }}
            className="w-full h-full"
            classNames={{
              base: "w-full h-full border-none font-inter bg-primary",
              header: "bg-primary !text-textColor border border-borderPrimary",
              headerWrapper: "bg-secondaryColor text-textColor",
              gridHeaderRow: "bg-secondaryColor text-textColor",
              gridBody: "bg-primary text-textColor",
              title: "text-textColor",
              cellButton:
                "text-textColor data-[selected]:bg-secondaryColor data-[selected]:text-textColor data-[selected]:hover:bg-secondaryColor data-[selected]:hover:text-textColor data-[today]:bg-secondaryColor data-[today]:text-textColor data-[hover]:bg-secondaryColor data-[hover]:text-textColor",
              pickerWrapper: "bg-primary text-textHeadingColor",
              pickerHighlight: "bg-secondaryColor text-textColor",
            }}
            isDateUnavailable={(dateValue) => {
              const jsDate = new Date(dateValue.year, dateValue.month - 1, dateValue.day);
              const currentDate = dayjs();
              const targetDate = dayjs(jsDate);

              // Disable future dates
              if (targetDate.isAfter(currentDate, "day")) {
                return true;
              }

              if (!isAvailable(jsDate)) {
                return true; // Disable dates not in availableDataDatesArray
              }

              return false;
            }}
            topContent={
              isPickerOpen ? (
                <button
                  type="button"
                  aria-label="Close year & month picker"
                  onClick={() => setIsPickerOpen(false)}
                  className="absolute right-3 top-3 z-10 rounded-full p-1 hover:bg-secondaryColor"
                >
                  <X size={16} className="text-textSecondaryColor" />
                </button>
              ) : null
            }
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
